import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import BakeasyHeaderLabel from '../../components/lables/BakeasyHeaderLabel';
import BakeasyCollapsibleTable, {
  CollapsibleTableHeadCell,
} from '../../components/tables/BakeasyCollapsibleTable';
import { t } from 'i18next';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import { useNavigate } from 'react-router-dom';
import {
  BakeasyButton,
  BakeasyButtonPrint,
} from '../../components/buttons/BakeasyButton';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import BakeasySimpleTable from '../../components/tables/BakeasySimpleTable';
import BakeasyDivider from '../../components/common/BakeasyDivider';
import dayjs, { Dayjs } from 'dayjs';
import * as Dropdown from '../../components/dropdowns/BakeasySelect';
import { PRODUCT_TYPE_ARRAY_1 } from '../../constants/Constants';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import {
  createInnerProductionPlanHistory,
  createOuterProductionPlan,
  downloadOuterProduction,
  productionPlanState,
} from '../../redux_store/slices/productionPlanSlice';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import { formatDate } from '../../utils/DateUtils';
import {
  categoryState,
  clearCategoryState,
  fetchCategories,
} from '../../redux_store/slices/categorySlice';
import {
  BakeasyAutoCompleteCategoryA,
  BakeasyAutoCompleteProduct,
  BakeasyAutoCompleteSubCategoryA,
} from '../../components/dropdowns/BakeasyAutoComplete';
import {
  clearSubCategoryState,
  fetchSubCategories,
  subCategoryState,
} from '../../redux_store/slices/subCategorySlice';
import {
  clearProductState,
  fetchProducts,
  productsState,
} from '../../redux_store/slices/productsSlice';
import { clearRecipesState } from '../../redux_store/slices/recipeBuilderSlice';
import { fetchConfirmProducts } from '../../redux_store/slices/confirmProductionSlice';
import BakeasyDateRangePicker from '../../components/common/BakeasyDateRangePicker';
import utc from 'dayjs/plugin/utc';
import BakeasyDatePicker from '../../components/common/BakeasyDatePicker';
dayjs.extend(utc);

export const ProductionPlan: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [selectedStatus, setSelectedStatus] = useState<any>('');
  const [selectedProductType, setSelectedProductType] = useState<any>('All');
  const [selectedProductionReport, setSelectedProductionReport] =
    useState<any>('');
  const [storeList, setStoreList] = useState<any[]>([]);
  const [productionList, setProductionList] = useState<any[]>([]);
  const [productionHistoryList, setProductionHistoryList] = useState<any[]>([]);
  const [byStoreTableHeaders, setByStoreTableHeaders] = useState<any[]>([]);
  const [requestedDeliveryDate, setRequestedDeliveryDate] =
    useState<Dayjs | null>();
  const [selectedCategory, setSelectedCategory] = useState<any>(undefined);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<any>(undefined);
  const [selectedProduct, setSelectedProduct] = useState<any>(undefined);
  const [viewAllOrders, setViewAllOrders] = useState(false);
  const [requestedDeliveryFromDate, setRequestedDeliveryFromDate] =
    useState<Dayjs | null>(null);
  const [requestedDeliveryToDate, setRequestedDeliveryToDate] =
    useState<Dayjs | null>(null);
  const [ordersDueOnDate, setOrdersDueOnDate] = useState<Dayjs | null>(null);
  const [allOrdersDueOnDate, setAllOrdersDueOnDate] = useState<Dayjs | null>(
    null
  );

  let _categoryState = useAppSelector(categoryState);
  let _subCategoryState = useAppSelector(subCategoryState);
  let _productsState = useAppSelector(productsState);

  let { value, historyValue, filter } = useAppSelector(productionPlanState);

  useEffect(() => {
    let productType: any = 'productType' in filter && filter?.productType;
    if (productType) {
      setSelectedProductType(productType);
    } else {
      setSelectedProductType('All');
    }

    let productionReport: any =
      'productionReport' in filter && filter?.productionReport;
    if (productionReport) {
      setSelectedProductionReport(productionReport);
    } else {
      setSelectedProductionReport('');
    }

    let dateFilter: any = 'dateFilter' in filter && filter?.dateFilter;
    let from: any =
      'requestedDeliveryFromDate' in filter &&
      filter?.requestedDeliveryFromDate;
    let to: any =
      'requestedDeliveryToDate' in filter && filter?.requestedDeliveryToDate;
    if (dateFilter === 'dueOn') {
      if (to) {
        setOrdersDueOnDate(dayjs(to));
      }
      setAllOrdersDueOnDate(null);
      setRequestedDeliveryFromDate(null);
      setRequestedDeliveryToDate(null);
    } else if (dateFilter === 'allDueOn') {
      if (to) {
        setAllOrdersDueOnDate(dayjs(to));
      }
      setOrdersDueOnDate(null);
      setRequestedDeliveryFromDate(null);
      setRequestedDeliveryToDate(null);
    } else if (dateFilter === 'dateRange') {
      if (from) {
        setRequestedDeliveryFromDate(dayjs(from));
      }
      if (to) {
        setRequestedDeliveryToDate(dayjs(to));
      }
      setOrdersDueOnDate(null);
      setAllOrdersDueOnDate(null);
    }

    let categoryId: any = 'categoryId' in filter && filter?.categoryId;
    if (categoryId) {
      if (_categoryState?.value?.data?.length > 0) {
        let temp: any = _categoryState.value?.data?.find(
          (item: any) => item?._id === categoryId
        );
        handleCategorySelect(temp);
      }
    } else {
      setSelectedCategory(undefined);
    }

    let subCategoryId: any = 'subCategoryId' in filter && filter?.subCategoryId;
    if (subCategoryId) {
      if (_subCategoryState?.value?.data?.length > 0) {
        let temp: any = _subCategoryState.value?.data?.find(
          (item: any) => item?._id === subCategoryId
        );
        handleSubCategorySelect(temp);
      }
    } else {
      setSelectedSubCategory(undefined);
    }

    let productId: any = 'productId' in filter && filter?.productId;
    if (productId) {
      if (_productsState?.value?.data?.length > 0) {
        let temp: any = _productsState.value?.data?.find(
          (item: any) => item?._id === productId
        );
        handleProductSelect(temp);
      }
    } else {
      setSelectedProduct(undefined);
    }
  }, [filter]);

  useEffect(() => {
    // if (value?.length === 0) {
    //   dispatch(showErrorSnackbar({ message: t('NO_DATA_FOUND') }));
    //   return;
    // }
    if (value?.length > 0) {
      let productionReport: any = '';
      let filteredData: React.SetStateAction<any[]> = [];
      let byStoreCollumn: any = [];

      if (selectedProductionReport === '' && 'productionReport' in filter) {
        productionReport = filter?.productionReport;
      } else {
        productionReport = selectedProductionReport;
      }

      if (productionReport === 'By Product') {
        filteredData = value.map((item: any) => {
          return {
            id: item._id,
            icon: 'ICONN',
            onIconClick: () => {
              handleHistoryRowIconClick({ productId: item._id }, filter);
            },
            productName: item.productName,
            category: item.categoryName,
            subCategory: item.subCategoryName,
            store:
              item.storeNames?.length > 1
                ? item?.storeNames[0] + ',...'
                : item?.storeNames[0],
            totalOrder: item?.totalOrder,
            productType: item?.productType,
            recipe: (
              <BakeasyButton
                tooltipTitle={
                  item?.productType === 'Purchased'
                    ? t('RECIPE_BUILDER_NOT_AVAILABLE_FOR_PURCHASED_PRODUCTS')
                    : ''
                }
                label={t('RECIPE')}
                size={'small'}
                disabled={item?.productType === 'Purchased'}
                onClick={async (event: any) => {
                  let d: any = await newFilterDataPayload();
                  let payload: any = await newFilterDataPayload();
                  navigate('/recipeBuilder', {
                    state: { productId: item?._id, payload },
                  });
                }}
              />
            ),
            history: [],
          };
        });

        setProductionList(filteredData);
      } else if (productionReport === 'By Store') {
        filteredData = value?.map((item: any) => {
          item?.stores?.forEach((storeItem: any) => {
            if (
              byStoreCollumn?.findIndex(
                (store: { id: any }) => store.id === storeItem.storeName
              ) === -1
            ) {
              byStoreCollumn.push({
                id: storeItem?.storeName,
                label: storeItem?.storeName,
                width: '120px',
              });
            }
          });

          const storeQuantity = byStoreCollumn.reduce(
            (acc: any, store: { label: any; id: any }) => {
              const quantity =
                item?.stores?.find(
                  (strQnt: any) => strQnt.storeName === store.label
                )?.quantity || 0;
              acc[store.id] = quantity;
              return acc;
            },
            {}
          );

          return {
            id: item._id,
            productName: item?.productName,
            ...storeQuantity,
            totalOrder: item?.totalOrders,
            recipe: (
              <BakeasyButton
                tooltipTitle={
                  item?.productType === 'Purchased'
                    ? t('RECIPE_BUILDER_NOT_AVAILABLE_FOR_PURCHASED_PRODUCTS')
                    : ''
                }
                disabled={item?.productType === 'Purchased'}
                label={t('RECIPE')}
                size={'small'}
                onClick={async (event: any) => {
                  let d: any = await newFilterDataPayload();
                  let payload: any = await newFilterDataPayload();
                  navigate('/recipeBuilder', {
                    state: { productId: item?._id, payload: { ...payload } },
                  });
                }}
              />
            ),
          };
        });
        byStoreCollumn?.length > 0 &&
          setByStoreTableHeaders([
            { id: 'productName', label: 'PRODUCT_NAME', width: '200px' },
            ...byStoreCollumn,
            { id: 'totalOrder', label: 'TOTAL_ORDERS', width: '120px' },
            { id: 'recipe', label: 'RECIPE', width: '120px' },
          ]);
        setStoreList(filteredData);
      }
    }
  }, [value]);

  // useEffect(() => {
  //   let date: any = 'requestedDeliveryDate' in filter && filter?.requestedDeliveryDate;
  //   let datee: any = 'requestedDeliveryDatee' in filter && filter?.requestedDeliveryDatee;
  //   let dateFilter: any = 'dateFilter' in filter && filter?.dateFilter;

  //   'requestedDeliveryDate' in filter && setRequestedDeliveryDate(dayjs(datee));
  //   // 'productionReport' in filter && setSelectedProductionReport(filter?.productionReport);
  //   'status' in filter && setSelectedStatus(filter?.status);
  //   'productType' in filter && setSelectedProductType(filter?.productType);
  //   'selectedCategory' in filter && setSelectedCategory(filter?.selectedCategory);

  //   let productType: any = 'productType' in filter && filter?.productType;
  //   let productionReport: any = 'productionReport' in filter && filter?.productionReport;

  //   if (productType) {
  //     setSelectedProductType(productType);
  //   } else {
  //     setSelectedProductType('All');
  //   }

  //   if (productionReport) {
  //     setSelectedProductionReport(productionReport);
  //   } else {
  //     setSelectedProductionReport('');
  //   }

  //   let from: any = 'requestedDeliveryFromDate' in filter && filter?.requestedDeliveryFromDate;
  //   let to: any = 'requestedDeliveryToDate' in filter && filter?.requestedDeliveryToDate;
  //   if (from && to) {
  //     setDueDateRange([new DateObject(new Date(from)), new DateObject(to)]);
  //   }

  //   // if (dateFil)
  //   if (dateFilter === 'dueOn') {
  //     if (to) {
  //       setOrdersDueOnDate(dayjs(to));
  //     }
  //     setAllOrdersDueOnDate(null);
  //     setRequestedDeliveryFromDate(null);
  //     setRequestedDeliveryToDate(null);
  //     setDueDateRange([]);
  //   } else if (dateFilter === 'allDueOn') {
  //     if (to) {
  //       setAllOrdersDueOnDate(dayjs(to));
  //     }
  //     setOrdersDueOnDate(null);
  //     setRequestedDeliveryFromDate(null);
  //     setRequestedDeliveryToDate(null);
  //     setDueDateRange([]);
  //   } else if (dateFilter === 'dateRange') {
  //     if (from) {
  //       setRequestedDeliveryFromDate(dayjs(from));
  //     }
  //     if (to) {
  //       setRequestedDeliveryToDate(dayjs(to));
  //     }
  //     if (from && to) {
  //       setDueDateRange([new DateObject(new Date(from)), new DateObject(to)]);
  //     }
  //     setOrdersDueOnDate(null);
  //     setAllOrdersDueOnDate(null);
  //   }
  // }, [filter]);

  const checkValidity = (): boolean => {
    // if (!requestedDeliveryDate) {
    //   let msg = `${t('DUE_DATE_IS_REQUIRED')}`;
    //   dispatch(showErrorSnackbar({ message: msg }));
    //   return false;
    // }

    const isDateRangeSelected =
      requestedDeliveryFromDate || requestedDeliveryToDate;
    const isOrdersDueOnSelected = !!ordersDueOnDate;
    const isAllOrdersDueOnSelected = !!allOrdersDueOnDate;

    if (requestedDeliveryFromDate && !requestedDeliveryToDate) {
      dispatch(showErrorSnackbar({ message: t('DUE_TO_DATE_IS_REQUIRED') }));
      return false;
    }

    if (!requestedDeliveryFromDate && requestedDeliveryToDate) {
      dispatch(showErrorSnackbar({ message: t('DUE_FROM_DATE_IS_REQUIRED') }));
      return false;
    }

    if (
      !isOrdersDueOnSelected &&
      !isAllOrdersDueOnSelected &&
      !isDateRangeSelected
    ) {
      dispatch(
        showErrorSnackbar({ message: t('DUE_DATE_OR_DATE_RANGE_REQUIRED') })
      );
      return false;
    }

    // if (!dueDateRange) {
    //   let msg = `${t('DUE_DATE_IS_REQUIRED')}`;
    //   dispatch(showErrorSnackbar({ message: msg }));
    //   return false;
    // }

    // if (dueDateRange && !dueDateRange[0]) {
    //   let msg = `${t('DUE_FROM_DATE_IS_REQUIRED')}`;
    //   dispatch(showErrorSnackbar({ message: msg }));
    //   return false;
    // }

    // if (dueDateRange && !dueDateRange[1]) {
    //   let msg = `${t('DUE_TO_DATE_IS_REQUIRED')}`;
    //   dispatch(showErrorSnackbar({ message: msg }));
    //   return false;
    // }

    if (!selectedProductionReport) {
      let msg = `${t('PRODUCTION_REPORT_IS_REQUIRED')}`;
      dispatch(showErrorSnackbar({ message: msg }));
      return false;
    }

    if (!selectedProductType) {
      let msg = `${t('PRODUCTION_TYPE_IS_REQUIRED')}`;
      dispatch(showErrorSnackbar({ message: msg }));
      return false;
    }
    return true;
  };

  async function filterDataPayload() {
    let date: any =
      'requestedDeliveryDate' in filter && filter?.requestedDeliveryDate;
    let datee: any =
      'requestedDeliveryDatee' in filter && filter?.requestedDeliveryDatee;

    let requestedFromDate: any =
      'requestedDeliveryFromDate' in filter &&
      filter?.requestedDeliveryFromDate;
    let requestedToDate: any =
      'requestedDeliveryToDate' in filter && filter?.requestedDeliveryToDate;
    let dateFilter: any = 'dateFilter' in filter && filter?.dateFilter;

    if (dateFilter) {
      if (dateFilter === 'dateRange') {
        let from: any = formatDate(requestedDeliveryFromDate?.toString());
        let to: any = formatDate(requestedDeliveryToDate?.toString());
        if (requestedFromDate !== from) {
          requestedFromDate = from;
        }
        if (requestedToDate !== to) {
          requestedToDate = to;
        }
      }
      if (dateFilter === 'dueOn') {
      }
      if (dateFilter === 'allDueOn') {
      }
    } else {
      let isDateRangeSelected: any =
        requestedDeliveryFromDate && requestedDeliveryToDate;
    }
    // if (requestedFromDate && requestedToDate) {
    //   setDueDateRange([
    //     new DateObject(new Date(requestedFromDate)),
    //     new DateObject(new Date(requestedToDate))
    //   ]);
    // }

    // let estDate3 = dayjs(datee)
    //   .tz('America/New_York')
    //   .format('YYYY-MM-DD');

    // if (requestedFromDate === false) {
    //   requestedFromDate = formatDate(dueDateRange[0]?.toString());
    //   //requestedFromDate = formatDate(requestedDeliveryFromDate?.toString());
    // }

    // if (requestedToDate === false) {
    //   requestedToDate = formatDate(dueDateRange[1]?.toString());
    //   //requestedToDate = formatDate(requestedDeliveryToDate?.toString());
    // }
    // if (
    //   requestedFromDate !== formatDate(dueDateRange[0]?.toString()) &&
    //   dueDateRange[0] !== undefined
    // ) {
    //   requestedFromDate = formatDate(dueDateRange[0]?.toString());
    // }
    // if (
    //   requestedToDate !== formatDate(dueDateRange[1]?.toString()) &&
    //   dueDateRange[1] !== undefined
    // ) {
    //   requestedToDate = formatDate(dueDateRange[1]?.toString());
    // }

    if (requestedFromDate === false) {
      requestedFromDate = formatDate(requestedDeliveryFromDate?.toString());
    }

    if (requestedToDate === false) {
      requestedToDate = formatDate(requestedDeliveryToDate?.toString());
    }

    if (
      requestedDeliveryFromDate === undefined ||
      requestedDeliveryFromDate === null
    ) {
      requestedFromDate = undefined;
    }

    if (
      requestedFromDate !== requestedDeliveryFromDate &&
      requestedDeliveryFromDate !== undefined &&
      requestedDeliveryFromDate !== null
    ) {
      requestedFromDate = formatDate(requestedDeliveryFromDate?.toString());
    }

    if (
      requestedDeliveryToDate === undefined ||
      requestedDeliveryToDate === null
    ) {
      requestedToDate = undefined;
    }

    if (
      requestedToDate !== requestedDeliveryToDate &&
      requestedDeliveryToDate !== undefined &&
      requestedDeliveryToDate !== null
    ) {
      requestedToDate = formatDate(requestedDeliveryToDate?.toString());
    }

    // let d: any = formatDate(dueDateRange[0]?.toString());
    // let d: any = formatDate(dueDateRange[0]?.toString());

    // if (date === undefined) {
    //   date = formatDate(requestedDeliveryDate?.toString());
    // }

    // if (requestedFromdate === false) {
    //   requestedFromdate = formatDate(requestedDeliveryDateFrom?.toString());
    // }

    // if (
    //   requestedFromdate !== requestedDeliveryDateFrom &&
    //   requestedDeliveryDateFrom !== undefined
    // ) {
    //   requestedFromdate = formatDate(requestedDeliveryDateFrom?.toString());
    // }

    // if (requestedFromdate === undefined) {
    //   requestedFromdate = formatDate(requestedDeliveryDateFrom?.toString());
    // }

    // if (requestedTodate === false) {
    //   requestedTodate = formatDate(requestedDeliveryDateTo?.toString());
    // }

    // if (requestedTodate !== requestedDeliveryDateTo && requestedDeliveryDateTo !== undefined) {
    //   requestedTodate = formatDate(requestedDeliveryDateTo?.toString());
    // }

    // if (requestedTodate === undefined) {
    //   requestedTodate = formatDate(requestedDeliveryDateTo?.toString());
    // }

    // if (!requestedDeliveryFromDate) requestedFromDate = undefined;
    // if (!requestedDeliveryToDate) requestedToDate = undefined;

    const isDateRangeSelected =
      requestedDeliveryFromDate && requestedDeliveryToDate;

    const isSingleDateSelected = ordersDueOnDate || allOrdersDueOnDate;

    let deliveryDate: any = ordersDueOnDate || allOrdersDueOnDate;

    if (isDateRangeSelected === null) {
      if (isSingleDateSelected === null) {
        if (deliveryDate === null) {
          let tempDate: any =
            'requestedDeliveryToDate' in filter &&
            filter?.requestedDeliveryToDate;
          switch (dateFilter) {
            case 'dueOn':
            case 'allDueOn':
              deliveryDate = tempDate;
              break;
          }
        }
      }
    }

    let payload: any = {
      //requestedDeliveryDate: dayjs(date).toISOString(),
      // requestedDeliveryDate: date,
      requestedDeliveryDatee: requestedDeliveryDate?.valueOf(),

      viewAllOrders,
      // requestedDeliveryFromDate: requestedFromdate,
      // requestedDeliveryToDate: requestedTodate,

      ...(selectedProductType &&
        selectedProductType !== 'All' && { productType: selectedProductType }),
      ...(selectedProductionReport && {
        productionReport: selectedProductionReport,
      }),
      ...(selectedStatus && { status: selectedStatus }),
      ...(selectedProduct && { productId: selectedProduct?._id }),
      ...(selectedCategory && { categoryId: selectedCategory?._id }),
      ...(selectedSubCategory && { subCategoryId: selectedSubCategory?._id }),
    };

    // let requestedFromDate: any = formatDate(dueDateRange[0]?.toString());
    // let requestedToDate: any = formatDate(dueDateRange[1]?.toString());

    let newDateFilter: any = isDateRangeSelected
      ? 'dateRange'
      : ordersDueOnDate
        ? 'dueOn'
        : 'allDueOn';

    // payload = {
    //   ...payload,
    //   requestedDeliveryFromDate: requestedFromDate,
    //   requestedDeliveryToDate: requestedToDate
    // };

    payload.dateFilter = newDateFilter;
    if (newDateFilter === 'allDueOn') {
      payload.viewAllOrders = true;
    } else {
      payload.viewAllOrders = false;
    }

    if (isSingleDateSelected && deliveryDate) {
      payload.requestedDeliveryToDate =
        typeof deliveryDate === 'string'
          ? deliveryDate
          : formatDate(deliveryDate?.toString());
    } else if (isDateRangeSelected) {
      payload.requestedDeliveryFromDate = requestedFromDate;
      payload.requestedDeliveryToDate = requestedToDate;
    }

    return payload;
  }

  async function newFilterDataPayload(tempFilter?: {} | undefined) {
    let payload: any = filter;
    // if (payload === null || payload === undefined || Object.keys(payload).length === 0) {
    payload = {
      ...filter,
      //requestedDeliveryDatee: requestedDeliveryDate?.valueOf(),
      //status: 'Processed',
      //viewAllOrders,
      //productType: selectedProductType !== "",
      productionReport:
        selectedProductionReport === ''
          ? 'productionReport' in filter && filter?.productionReport
          : selectedProductionReport,
      productType:
        selectedProductType === ''
          ? 'productType' in filter && filter?.productType
          : selectedProductType,
      categoryId: selectedCategory
        ? selectedCategory?._id
        : 'categoryId' in filter && filter?.categoryId,
      subCategoryId: selectedSubCategory
        ? selectedSubCategory?._id
        : 'subCategoryId' in filter && filter?.subCategoryId,
      productId: selectedProduct
        ? selectedProduct?._id
        : 'productId' in filter && filter?.productId,
      // ...(selectedProductType &&
      //   selectedProductType !== 'All' && { productType: selectedProductType }),
      // ...(selectedProductType &&
      //   selectedProductType !== 'All' && { productType: selectedProductType }),
      //...(selectedProductionReport && { productionReport: selectedProductionReport }),
      // ...(selectedStatus && { status: selectedStatus }),
      //...(selectedProductType && { productType: selectedProductType }),
      //...(selectedProduct && { productId: selectedProduct?._id })
      //...(selectedCategory && { categoryId: selectedCategory?._id }),
      //...(selectedSubCategory && { subCategoryId: selectedSubCategory?._id })
    };
    // } else {
    //   payload = {
    //     ...payload,
    //     productionReport: selectedProductionReport,
    //     productType: selectedProductType
    //   };
    // }

    if (requestedDeliveryFromDate && requestedDeliveryToDate) {
      let from: any = formatDate(requestedDeliveryFromDate?.toString());
      let to: any = formatDate(requestedDeliveryToDate?.toString());
      payload = {
        ...payload,
        dateFilter: 'dateRange',
        viewAllOrders: false,
        requestedDeliveryFromDate: from,
        requestedDeliveryToDate: to,
      };
    }

    if (ordersDueOnDate) {
      let to: any = formatDate(ordersDueOnDate?.toString());
      payload = {
        ...payload,
        dateFilter: 'dueOn',
        viewAllOrders: false,
        requestedDeliveryFromDate: undefined,
        requestedDeliveryToDate: to,
      };
    }

    if (allOrdersDueOnDate) {
      let to: any = formatDate(allOrdersDueOnDate?.toString());
      payload = {
        ...payload,
        dateFilter: 'allDueOn',
        viewAllOrders: true,
        requestedDeliveryFromDate: undefined,
        requestedDeliveryToDate: to,
      };
    }

    if (payload?.productType === 'All' || payload?.productType === '') {
      delete payload.productType;
    }

    if (!selectedCategory) {
      delete payload.categoryId;
    }

    if (!selectedSubCategory) {
      delete payload.subCategoryId;
    }

    if (!selectedProduct) {
      delete payload.productId;
    }

    return payload;
  }

  const handleGenerateClick = async () => {
    let d: any = await newFilterDataPayload();

    if (!checkValidity()) {
      return;
    }
    try {
      let payload = await newFilterDataPayload();
      let response: any = await dispatch(createOuterProductionPlan(payload));
      if (response?.payload?.data?.length === 0) {
        dispatch(showErrorSnackbar({ message: t('NO_DATA_FOUND') }));
      }
    } catch (err) {}
  };

  const handleHistoryRowIconClick = async (productId: any, filter?: {}) => {
    try {
      let d: any = await newFilterDataPayload(filter);
      let payload = await newFilterDataPayload();
      const newPayload = { ...productId, ...payload };
      //delete newPayload.productionReport;

      await dispatch(createInnerProductionPlanHistory(newPayload));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (historyValue) {
      const filteredDataa = historyValue?.stores?.map((store: any) => {
        return {
          storeName: store?.storeName,
          orderId: store?.orderId,
          totalOrders: store?.totalOrder,
          recipe: (
            <BakeasyButton
              label={t('RECIPE269')}
              size="small"
              onClick={(event: any) => {
                navigate('/recipeBuilder');
              }}
            />
          ),
        };
      });

      setProductionHistoryList(filteredDataa);

      // if (filteredDataa.length > 0 && productionList?.length > 0) {
      if (
        filteredDataa &&
        filteredDataa.length > 0 &&
        productionList?.length > 0
      ) {
        let p1: any = productionList?.map(
          (production: any) => production?._id === historyValue?._id
        );
        p1['history'] = filteredDataa;

        let sdf = productionList;
        sdf?.forEach((production: any) => {
          if (production?.id === historyValue?._id) {
            production.history = filteredDataa;
          }
          return production;
        });

        setProductionList(sdf);
      }
    }
  }, [historyValue]);

  async function handleCategorySelect(category: any) {
    setSelectedSubCategory(undefined);
    if (category === undefined || category === null) {
      setSelectedCategory(undefined);
      setSelectedProduct(undefined);
      return;
    }

    setSelectedCategory(category);
    await dispatch(
      fetchSubCategories({ page: 1, limit: 5, categoryId: category?._id })
    );
  }

  async function handleSubCategorySelect(subCategory: any) {
    setSelectedProduct(undefined);
    if (subCategory === undefined || subCategory === null) {
      setSelectedProduct(undefined);
      return;
    }

    setSelectedSubCategory(subCategory);

    let catId: any = selectedCategory
      ? selectedCategory?._id
      : 'categoryId' in filter && filter?.categoryId;

    let subCatId: any = subCategory
      ? subCategory?._id
      : 'subCategoryId' in filter && filter?.subCategoryId;

    await dispatch(
      fetchProducts({
        page: 1,
        limit: 5,
        //categoryId: selectedCategory?._id,
        categoryId: catId,
        //subCategoryId: subCategory?._id
        subCategoryId: subCatId,
      })
    );
  }

  useEffect(() => {
    dispatch(clearCategoryState());
    dispatch(clearSubCategoryState());
    dispatch(clearProductState());
    dispatch(fetchCategories({ page: 1, limit: 10 }));
  }, []);

  async function handleProductSelect(product: any) {
    setSelectedProduct(product);
  }

  const tableHeaders: CollapsibleTableHeadCell[] = [
    { id: 'storeName', label: 'STORE_NAME', width: '200px' },
    { id: 'totalOrders', label: 'TOTAL_ORDERS', width: '120px' },
    { id: 'orderId', label: 'ORDER_ID' },
  ];

  const reportTableHeaders: CollapsibleTableHeadCell[] = [
    { id: 'icon', label: '', width: '40px' },
    { id: 'productName', label: 'PRODUCT_NAME', width: '190px' },
    { id: 'category', label: 'CATEGORY', width: '190px' },
    { id: 'subCategory', label: 'SUB_CATEGORY', width: '190px' },
    { id: 'store', label: 'STORE', width: '220px' },
    { id: 'totalOrder', label: 'TOTAL_ORDERS', width: '120px' },
    { id: 'recipe', label: 'RECIPE', width: '120px' },
  ];

  function productionPlanUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('PRODUCTION_PLAN_PREVIEW')} />

          <Grid
            container
            spacing={2}
            //sx={{ alignItems: 'center' }}
          >
            {/* <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyDueDatePicker
                label={t('DUE_DATE')}
                required={true}
                value={requestedDeliveryDate}
                onChange={(e: any, params) => {
                  setProductionList([]);
                  setStoreList([]);
                  setRequestedDeliveryDate(e);
                }}
                onClick={(e: any, params) => {
                  setViewAllOrders(params);
                }}
              />
            </Grid> */}

            <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
              <BakeasyDateRangePicker
                // label="Due Dates"
                requestedDeliveryDateFrom={requestedDeliveryFromDate}
                requestedDeliveryDateTo={requestedDeliveryToDate}
                // onRequestedDeliveryDateFromChange={val => setRequestedDeliveryFromDate(val)}
                onRequestedDeliveryDateFromChange={(value: any) => {
                  // handleFromDateChange(value);
                  setRequestedDeliveryFromDate(value);
                  setOrdersDueOnDate(null);
                  setAllOrdersDueOnDate(null);
                  setProductionList([]);
                  setStoreList([]);
                }}
                onRequestedDeliveryDateToChange={(value: any) => {
                  setRequestedDeliveryToDate(value);
                  setOrdersDueOnDate(null);
                  setAllOrdersDueOnDate(null);
                  setProductionList([]);
                  setStoreList([]);
                }}

                // onClick={(e: any, params: any) => {
                //   setViewAllOrders(params);
                //   if (params) {
                //     handleFromDateChange(null);
                //   }
                // }}
              />
            </Grid>

            <Grid item sx={{ alignContent: 'end', paddingBottom: 1 }}>
              <Typography sx={{ fontWeight: 'bold' }}>{'OR'}</Typography>
            </Grid>

            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <BakeasyDatePicker
                label={t('ORDERS_DUE_ON')}
                required={true}
                // value={requestedDeliveryDate}
                value={ordersDueOnDate}
                onChange={(e: any, params) => {
                  setOrdersDueOnDate(e);
                  setRequestedDeliveryFromDate(null);
                  setRequestedDeliveryToDate(null);
                  setAllOrdersDueOnDate(null);
                  setProductionList([]);
                  setStoreList([]);
                  // setRequestedDeliveryDate(e);
                }}
              />
            </Grid>

            <Grid item sx={{ alignContent: 'end', paddingBottom: 1 }}>
              <Typography sx={{ fontWeight: 'bold' }}>{'OR'}</Typography>
            </Grid>

            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <BakeasyDatePicker
                label={t('ALL_ORDERS_DUE_ON')}
                required={true}
                // value={requestedDeliveryDate}
                value={allOrdersDueOnDate}
                onChange={(e: any, params) => {
                  setAllOrdersDueOnDate(e);
                  setRequestedDeliveryFromDate(null);
                  setRequestedDeliveryToDate(null);
                  setOrdersDueOnDate(null);
                  setProductionList([]);
                  setStoreList([]);
                  // setRequestedDeliveryDate(e);
                }}
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyDateRangePickerR
                required={true}
                label={t('DUE_DATE')}
                value={dueDateRange}
                onChange={(newValue: any) => {
                  setDueDateRange(newValue);
                }}
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3} lg={2} xl={2}>
              <BakeasyMultiSelect
                disabled={false}
                label={t("STORE")}
                size={"small"}
                fullWidth={true}
                statusOptions={storeOptions}
                onChange={handleChangeOptions}
                selectedStatus={selectedStatus}
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <Dropdown.Status
                options={PRODUCTION_STATUS_ARRAY}
                value={selectedStatus}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedStatus(event?.target?.value);
                }}
              />
            </Grid> */}

            <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
              <Dropdown.ProductType
                options={PRODUCT_TYPE_ARRAY_1}
                value={selectedProductType}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedProductType(event?.target?.value);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <Dropdown.ProductReportType
                value={selectedProductionReport}
                required={true}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedProductionReport(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <BakeasyAutoCompleteCategoryA
                value={selectedCategory}
                required={false}
                options={_categoryState.value?.data}
                onChange={(newValue: any) => {
                  // setProductionList([]);
                  // setStoreList([]);
                  if (typeof newValue === 'string' && newValue?.length > 2) {
                    dispatch(
                      fetchCategories({ page: 1, limit: 5, search: newValue })
                    );
                  }

                  if (newValue === null) {
                    dispatch(fetchCategories({ page: 1, limit: 5 }));
                  }

                  if (typeof newValue === 'object') {
                    handleCategorySelect(newValue);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <BakeasyAutoCompleteSubCategoryA
                value={selectedSubCategory}
                required={false}
                options={_subCategoryState.value?.data}
                onChange={(newValue: any) => {
                  // setProductionList([]);
                  // setStoreList([]);

                  // if (typeof newValue === 'string' && newValue?.length === 0) {
                  //   handleSubCategorySelect(newValue);
                  // }
                  if (
                    (newValue === '' || newValue === null) &&
                    selectedCategory?._id
                  ) {
                    setSelectedSubCategory(undefined);
                    dispatch(
                      fetchSubCategories({
                        page: 1,
                        limit: 5,
                        categoryId: selectedCategory._id,
                      })
                    );
                    return;
                  }

                  if (typeof newValue === 'object') {
                    handleSubCategorySelect(newValue);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2.5} lg={2.5} xl={2.5}>
              <BakeasyAutoCompleteProduct
                value={selectedProduct}
                required={false}
                options={_productsState.value?.data}
                onChange={(newValue: any) => {
                  // setProductionList([]);
                  // setStoreList([]);
                  handleProductSelect(newValue);
                }}
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyAutoCompleteCategoryA
                value={selectedCategory}
                required={false}
                options={_categoryState.value?.data}
                onChange={(newValue: any) => {
                  setProductionList([]);
                  setStoreList([]);

                  if (typeof newValue === 'string' && newValue?.length > 2) {
                    dispatch(fetchCategories({ page: 1, limit: 5, search: newValue }));
                  }

                  if (newValue === null) {
                    dispatch(fetchCategories({ page: 1, limit: 5 }));
                  }

                  if (typeof newValue === 'object') {
                    handleCategorySelect(newValue);
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyAutoCompleteSubCategoryA
                value={selectedSubCategory}
                required={false}
                options={_subCategoryState.value?.data}
                onChange={(newValue: any) => {
                  setProductionList([]);
                  setStoreList([]);

                  // if (typeof newValue === 'string' && newValue?.length === 0) {
                  //   handleSubCategorySelect(newValue);
                  // }
                  if ((newValue === '' || newValue === null) && selectedCategory?._id) {
                    setSelectedSubCategory(undefined);
                    dispatch(fetchSubCategories({ page: 1, limit: 5, categoryId: selectedCategory._id }));
                    return;
                  }
                  if (typeof newValue === 'object') {
                    handleSubCategorySelect(newValue);
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyAutoCompleteProduct
                value={selectedProduct}
                required={false}
                options={_productsState.value?.data}
                onChange={(newValue: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  handleProductSelect(newValue);
                }}
              />
            </Grid> 

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              lg={3}
              xl={3}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <BakeasyButton
                label={t('GENERATE')}
                onClick={async () => {
                  setProductionList([]);
                  setStoreList([]);
                  await handleGenerateClick();
                }}
              />
            </Grid>*/}
          </Grid>

          <Grid container spacing={2} sx={{ marginTop: 0 }}>
            {/* <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <BakeasyDueDatePicker
                label={t('DUE_DATE')}
                required={true}
                value={requestedDeliveryDate}
                onChange={(e: any, params) => {
                  setProductionList([]);
                  setStoreList([]);
                  setRequestedDeliveryDate(e);
                }}
                onClick={(e: any, params) => {
                  setViewAllOrders(params);
                }}
              />
            </Grid>

            
            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <Dropdown.Status
                options={PRODUCTION_STATUS_ARRAY}
                value={selectedStatus}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedStatus(event?.target?.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <Dropdown.ProductType
                options={PRODUCT_TYPE_ARRAY_1}
                value={selectedProductType}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedProductType(event?.target?.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <Dropdown.ProductReportType
                value={selectedProductionReport}
                required={true}
                onChange={(event: any) => {
                  setProductionList([]);
                  setStoreList([]);
                  setSelectedProductionReport(event.target.value);
                }}
              />
            </Grid>
           */}

            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'end',
              }}
            >
              <BakeasyButton
                label={t('GENERATE')}
                onClick={async () => {
                  setProductionList([]);
                  setStoreList([]);
                  await handleGenerateClick();
                }}
              />
            </Grid>
          </Grid>

          <BakeasyDivider />

          {selectedProductionReport === 'By Product' && (
            <Grid container>
              <Grid item xs={12}>
                {productionList?.length > 0 ? (
                  <BakeasyCollapsibleTable
                    reportTableHeaders={reportTableHeaders}
                    tableHeaders={tableHeaders}
                    rowsData={productionList}
                  />
                ) : (
                  <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
                )}
              </Grid>
            </Grid>
          )}

          {selectedProductionReport === 'By Store' && (
            <>
              <Grid mt={1}>
                {storeList.length > 0 ? (
                  <BakeasySimpleTable
                    headers={byStoreTableHeaders}
                    tableData={storeList}
                  />
                ) : (
                  <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
                )}
              </Grid>
            </>
          )}

          {/* {productionList?.length > 0 && ( */}
          {(productionList?.length > 0 || storeList.length > 0) && (
            <Grid
              item
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
            >
              <Grid container sx={{ alignItems: 'end', justifyContent: 'end' }}>
                <Grid marginTop={1}>
                  <div className="tableOptions">
                    <BakeasyButton
                      label={t('PREVIEW_CONSOLIDATED_RECIPE_BUILDER')}
                      onClick={async function (
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ): Promise<void> {
                        if (!checkValidity()) {
                          return;
                        }
                        let d: any = await newFilterDataPayload();
                        let payload = await newFilterDataPayload();
                        dispatch(clearRecipesState());
                        navigate('/consolidatedRecipeBuilder', {
                          state: { ...payload },
                        });
                      }}
                    />
                    <BakeasyButton
                      label={t('CONFIRM_PRODUCTION')}
                      onClick={async function (
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ): Promise<void> {
                        let payload = await newFilterDataPayload();
                        delete payload?.requestedDeliveryDatee;
                        await dispatch(fetchConfirmProducts(payload));
                        setProductionList([]);
                        setStoreList([]);
                      }}
                    />
                    {selectedProductionReport === 'By Product' && (
                      <>
                        <BakeasyButtonPrint
                          onClick={async function (
                            event: React.MouseEvent<
                              HTMLButtonElement,
                              MouseEvent
                            >
                          ): Promise<void> {
                            let d: any = await newFilterDataPayload();
                            let payload = await newFilterDataPayload();
                            dispatch(
                              downloadOuterProduction({
                                ...payload,
                                downloadType: 'pdf',
                              })
                            );
                          }}
                        />
                      </>
                    )}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          )}
        </BakeasyPaper>
      </Grid>
    );
  }

  return productionPlanUI();
};
